// EUNORAU quantity reward cart - drawer auto-sync and rerender 2026-09-23
(() => {
  if (window.__eunQuantityRewardCartLoaded) return;
  window.__eunQuantityRewardCartLoaded = true;

  const root = () => window.Shopify?.routes?.root || '/';
  const settings = () => window.MinimogSettings || {};
  let lastCart = null;
  let syncing = false;
  let rerunRequested = false;
  let timer = 0;
  let settleTimer = 0;
  let subscribed = false;
  let initialized = false;

  const property = (item, key) => String(item?.properties?.[key] || '');
  const positiveInteger = (value, fallback = 0) => {
    const parsed = Number.parseInt(String(value || ''), 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
  };

  function isQuantityQualifier(item) {
    return property(item, '_quantity_reward_qualifier').toLowerCase() === 'true'
      && property(item, '_free_gift').toLowerCase() !== 'true';
  }

  function isQuantityGift(item) {
    return property(item, '_quantity_reward_gift').toLowerCase() === 'true'
      && property(item, '_free_gift').toLowerCase() === 'true';
  }

  function isQuantityAccessoryGift(item) {
    return property(item, '_quantity_reward_accessory_gift').toLowerCase() === 'true'
      && property(item, '_free_gift').toLowerCase() === 'true';
  }

  function isGiftQuantityQualifier(item) {
    return property(item, '_gift_quantity_qualifier').toLowerCase() === 'true'
      && property(item, '_free_gift').toLowerCase() !== 'true';
  }

  function isGiftQuantityGift(item) {
    return property(item, '_gift_quantity_reward').toLowerCase() === 'true'
      && property(item, '_free_gift').toLowerCase() === 'true';
  }

  function isAnyQuantityGift(item) {
    return isQuantityGift(item) || isQuantityAccessoryGift(item) || isGiftQuantityGift(item);
  }

  function groupData(item) {
    const productId = property(item, '_quantity_reward_group') || property(item, '_qualifying_product_id');
    const variantId = positiveInteger(property(item, '_qualifying_variant_id'), Number(item.variant_id));
    const buyQuantity = positiveInteger(property(item, '_quantity_reward_buy'), 10);
    const rewardQuantity = positiveInteger(property(item, '_quantity_reward_get'), 1);
    return {
      key: `${productId}|${variantId}|${buyQuantity}|${rewardQuantity}`,
      productId,
      variantId,
      buyQuantity,
      rewardQuantity
    };
  }

  async function getCart() {
    const response = await fetch(`${root()}cart.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    });
    if (!response.ok) throw new Error('Unable to read the cart.');
    return response.json();
  }

  function withCartDrawerSections(body) {
    const cartDrawer = document.querySelector('m-cart-drawer');
    if (!cartDrawer || typeof cartDrawer.getSectionsToRender !== 'function') return body;

    return {
      ...body,
      sections: cartDrawer.getSectionsToRender().map(section => section.id),
      sections_url: window.location.pathname
    };
  }

  function renderCartDrawer(result) {
    const cartDrawer = document.querySelector('m-cart-drawer');
    if (cartDrawer && result?.sections && typeof cartDrawer.renderContents === 'function') {
      cartDrawer.renderContents(result);
    }
  }

  async function updateCart(updates) {
    const response = await fetch(settings().routes?.cart_update_url || `${root()}cart/update.js`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      credentials: 'same-origin',
      body: JSON.stringify(withCartDrawerSections({ updates }))
    });
    const result = await response.json();
    if (!response.ok || result.status) {
      throw new Error(result.description || result.message || 'Unable to update the free item.');
    }
    return result;
  }

  async function addCartItems(items) {
    const response = await fetch(settings().routes?.cart_add_url || `${root()}cart/add.js`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      credentials: 'same-origin',
      body: JSON.stringify(withCartDrawerSections({ items }))
    });
    const result = await response.json();
    if (!response.ok || result.status) {
      throw new Error(result.description || result.message || 'Unable to add the free item.');
    }
    return result;
  }

  function buildGroups(cart) {
    const groups = new Map();

    (cart.items || []).forEach(item => {
      if (!isQuantityQualifier(item) && !isQuantityGift(item)) return;
      const data = groupData(item);
      if (!data.productId || !data.variantId) return;

      if (!groups.has(data.key)) {
        groups.set(data.key, { ...data, paidQuantity: 0, gifts: [] });
      }

      const group = groups.get(data.key);
      if (isQuantityGift(item)) {
        group.gifts.push(item);
      } else {
        group.paidQuantity += Number(item.quantity) || 0;
      }
    });

    return groups;
  }

  function buildAccessoryGroups(cart) {
    const qualifierGroups = new Map();
    const groups = new Map();

    (cart.items || []).forEach(item => {
      if (!isQuantityQualifier(item)) return;
      const productId = property(item, '_quantity_reward_group');
      const qualifyingVariantId = Number(item.variant_id);
      const accessoryVariantId = positiveInteger(property(item, '_quantity_reward_accessory_variant_id'), 0);
      if (!productId || !qualifyingVariantId) return;

      const baseKey = `${productId}|${qualifyingVariantId}`;
      if (!qualifierGroups.has(baseKey)) {
        qualifierGroups.set(baseKey, { productId, qualifyingVariantId, accessoryVariantId: 0, paidQuantity: 0 });
      }
      const qualifier = qualifierGroups.get(baseKey);
      qualifier.paidQuantity += Number(item.quantity) || 0;
      if (accessoryVariantId) qualifier.accessoryVariantId = accessoryVariantId;
    });

    qualifierGroups.forEach(qualifier => {
      if (!qualifier.accessoryVariantId) return;
      const key = `${qualifier.productId}|${qualifier.qualifyingVariantId}|${qualifier.accessoryVariantId}`;
      groups.set(key, { ...qualifier, key, gifts: [] });
    });

    (cart.items || []).forEach(item => {
      if (!isQuantityAccessoryGift(item)) return;
      const productId = property(item, '_quantity_reward_group') || property(item, '_qualifying_product_id');
      const qualifyingVariantId = positiveInteger(property(item, '_quantity_reward_qualifying_variant_id'), 0);
      const accessoryVariantId = positiveInteger(property(item, '_quantity_reward_accessory_variant_id'), Number(item.variant_id));
      if (!productId || !qualifyingVariantId || !accessoryVariantId) return;

      const key = `${productId}|${qualifyingVariantId}|${accessoryVariantId}`;
      if (!groups.has(key)) {
        groups.set(key, { key, productId, qualifyingVariantId, accessoryVariantId, paidQuantity: 0, gifts: [] });
      }
      groups.get(key).gifts.push(item);
    });

    return groups;
  }

  function buildGiftQuantityGroups(cart) {
    const groups = new Map();

    (cart.items || []).forEach(item => {
      if (!isGiftQuantityGift(item)) return;
      const productId = property(item, '_qualifying_product_id') || property(item, '_gift_quantity_group');
      const giftVariantId = positiveInteger(property(item, '_gift_quantity_variant_id'), Number(item.variant_id));
      if (!productId || !giftVariantId) return;

      if (!groups.has(productId)) {
        groups.set(productId, {
          productId,
          giftVariantId,
          buyQuantity: positiveInteger(property(item, '_gift_quantity_buy'), 1),
          rewardQuantity: positiveInteger(property(item, '_gift_quantity_get'), 1),
          paidQuantity: 0,
          gifts: []
        });
      }
      groups.get(productId).gifts.push(item);
    });

    (cart.items || []).forEach(item => {
      if (!isGiftQuantityQualifier(item)) return;
      const productId = property(item, '_gift_quantity_group');
      const giftVariantId = positiveInteger(property(item, '_gift_quantity_variant_id'), 0);
      if (!productId || !giftVariantId || groups.has(productId)) return;

      groups.set(productId, {
        productId,
        giftVariantId,
        buyQuantity: positiveInteger(property(item, '_gift_quantity_buy'), 1),
        rewardQuantity: positiveInteger(property(item, '_gift_quantity_get'), 1),
        paidQuantity: 0,
        gifts: []
      });
    });

    groups.forEach(group => {
      group.paidQuantity = (cart.items || [])
        .filter(item => property(item, '_free_gift').toLowerCase() !== 'true' && String(item.product_id) === group.productId)
        .reduce((total, item) => total + (Number(item.quantity) || 0), 0);
    });

    return groups;
  }

  function giftProperties(group) {
    return {
      _free_gift: 'true',
      _quantity_reward_gift: 'true',
      _qualifying_product_id: group.productId,
      _qualifying_variant_id: String(group.variantId),
      _quantity_reward_group: group.productId,
      _quantity_reward_buy: String(group.buyQuantity),
      _quantity_reward_get: String(group.rewardQuantity)
    };
  }

  function accessoryGiftProperties(group) {
    return {
      _free_gift: 'true',
      _quantity_reward_accessory_gift: 'true',
      _qualifying_product_id: group.productId,
      _quantity_reward_qualifying_variant_id: String(group.qualifyingVariantId),
      _quantity_reward_accessory_variant_id: String(group.accessoryVariantId),
      _quantity_reward_group: group.productId
    };
  }

  function giftQuantityProperties(group) {
    return {
      _free_gift: 'true',
      _gift_quantity_reward: 'true',
      _qualifying_product_id: group.productId,
      _gift_quantity_variant_id: String(group.giftVariantId),
      _gift_quantity_buy: String(group.buyQuantity),
      _gift_quantity_get: String(group.rewardQuantity)
    };
  }

  function markGiftControls(cart) {
    if (!cart?.items) return;
    const giftLines = new Set(
      cart.items
        .map((item, index) => isAnyQuantityGift(item) ? String(index + 1) : '')
        .filter(Boolean)
    );

    document.querySelectorAll('[data-cart-item][data-index]').forEach(node => {
      const isGiftLine = giftLines.has(String(node.dataset.index || ''));

      if (!isGiftLine) {
        node.classList.remove('eun-quantity-reward-gift');
        node.querySelectorAll('[data-eun-quantity-reward-badge]').forEach(badge => badge.remove());
        return;
      }

        node.classList.add('eun-quantity-reward-gift');

        node.querySelectorAll('m-quantity-input, m-cart-remove-button, .scd-item__remove').forEach(control => {
          control.classList.add('eun-quantity-reward-controls');
          control.setAttribute('aria-hidden', 'true');
        });

        node.querySelectorAll('[name="updates[]"], button').forEach(control => {
          if (control.closest('m-quantity-input') || control.closest('m-cart-remove-button')) {
            control.disabled = true;
            control.tabIndex = -1;
          }
        });

        const info = node.querySelector('.scd-item__info, .sf-cart__item-product-info');
        if (info && !info.querySelector('[data-eun-quantity-reward-badge]')) {
          const badge = document.createElement('span');
          badge.className = 'eun-quantity-reward-badge';
          badge.dataset.eunQuantityRewardBadge = '';
          badge.textContent = 'Free item';
          info.appendChild(badge);
        }
    });
  }

  function emitCartUpdate(cart) {
    if (window.MinimogEvents && window.MinimogTheme?.pubSubEvents?.cartUpdate) {
      window.MinimogEvents.emit(window.MinimogTheme.pubSubEvents.cartUpdate, {
        ...cart,
        source: 'quantity-reward-sync'
      });
    }
  }

  async function syncQuantityRewards() {
    if (syncing) {
      rerunRequested = true;
      return;
    }

    syncing = true;
    try {
      let cart = await getCart();
      const groups = buildGroups(cart);
      const accessoryGroups = buildAccessoryGroups(cart);
      const giftQuantityGroups = buildGiftQuantityGroups(cart);
      const updates = {};
      const additions = [];
      let drawerResult = null;

      groups.forEach(group => {
        const desired = Math.floor(group.paidQuantity / group.buyQuantity) * group.rewardQuantity;

        if (group.gifts.length) {
          group.gifts.forEach((gift, index) => {
            const target = index === 0 ? desired : 0;
            if (gift.key && Number(gift.quantity) !== target) updates[gift.key] = target;
          });
        } else if (desired > 0) {
          additions.push({
            id: group.variantId,
            quantity: desired,
            properties: giftProperties(group)
          });
        }
      });

      accessoryGroups.forEach(group => {
        const desired = group.paidQuantity;

        if (group.gifts.length) {
          group.gifts.forEach((gift, index) => {
            const target = index === 0 ? desired : 0;
            if (gift.key && Number(gift.quantity) !== target) updates[gift.key] = target;
          });
        } else if (desired > 0) {
          additions.push({
            id: group.accessoryVariantId,
            quantity: desired,
            properties: accessoryGiftProperties(group)
          });
        }
      });

      giftQuantityGroups.forEach(group => {
        const desired = Math.floor(group.paidQuantity / group.buyQuantity) * group.rewardQuantity;
        let keptMatchingGift = false;

        group.gifts.forEach(gift => {
          const isMatchingGift = String(gift.variant_id) === String(group.giftVariantId);
          const target = isMatchingGift && !keptMatchingGift ? desired : 0;
          if (isMatchingGift && !keptMatchingGift) keptMatchingGift = true;
          if (gift.key && Number(gift.quantity) !== target) updates[gift.key] = target;
        });

        if (!keptMatchingGift && desired > 0) {
          additions.push({
            id: group.giftVariantId,
            quantity: desired,
            properties: giftQuantityProperties(group)
          });
        }
      });

      let changed = false;
      if (Object.keys(updates).length) {
        cart = await updateCart(updates);
        drawerResult = cart;
        changed = true;
      }
      if (additions.length) {
        drawerResult = await addCartItems(additions);
        cart = await getCart();
        changed = true;
      }

      lastCart = cart;
      markGiftControls(cart);
      if (changed) {
        renderCartDrawer(drawerResult);
        emitCartUpdate(cart);
      }
    } catch (error) {
      console.error('[Quantity reward]', error);
    } finally {
      syncing = false;
      if (rerunRequested) {
        rerunRequested = false;
        scheduleSync(150);
      }
    }
  }

  function scheduleSync(delay = 500) {
    window.clearTimeout(timer);
    timer = window.setTimeout(syncQuantityRewards, delay);
  }

  function syncAfterDrawerQuantityChange(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (!target.closest('m-cart-drawer m-quantity-input, m-cart-drawer [name="updates[]"], m-cart-drawer m-cart-remove-button, m-cart-drawer .scd-item__remove')) return;

    scheduleSync(700);
    window.clearTimeout(settleTimer);
    settleTimer = window.setTimeout(() => scheduleSync(0), 1800);
  }

  function subscribeToThemeCart() {
    if (subscribed) return;
    if (!window.MinimogEvents || !window.MinimogTheme?.pubSubEvents?.cartUpdate) {
      window.setTimeout(subscribeToThemeCart, 250);
      return;
    }

    subscribed = true;
    window.MinimogEvents.subscribe(window.MinimogTheme.pubSubEvents.cartUpdate, event => {
      if (event?.source === 'quantity-reward-sync') {
        window.setTimeout(() => markGiftControls(lastCart), 250);
        return;
      }
      scheduleSync(450);
    });
  }

  const observer = new MutationObserver(mutations => {
    if (lastCart) markGiftControls(lastCart);

    const cartDrawerRendered = mutations.some(mutation => {
      const target = mutation.target;
      return target instanceof Element
        && (target.matches('m-cart-drawer') || target.closest('m-cart-drawer'));
    });

    if (cartDrawerRendered) scheduleSync(250);
  });

  function init() {
    if (initialized) return;
    initialized = true;
    subscribeToThemeCart();
    document.addEventListener('click', syncAfterDrawerQuantityChange);
    document.addEventListener('change', syncAfterDrawerQuantityChange);
    syncQuantityRewards();
    observer.observe(document.body, { childList: true, subtree: true });
  }

  document.addEventListener('DOMContentLoaded', init);

  if (document.readyState !== 'loading') init();
})();
